import { Building2, Check, Copy, Mail, Plus, ShieldAlert, Ticket } from "lucide-react";
import { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { StatusPill } from "../components/StatusPill";
import { useUser } from "../lib/auth";
import { admin as adminApi, type AdminCompany, type CompanyInvite } from "../lib/api";

const TIER_LABEL: Record<string, string> = {
  owner: "Platform owner",
  mentorship: "Mentorship",
  developer: "Developer",
};

function tierLabel(tier: string): string {
  return TIER_LABEL[tier] ?? tier;
}

function formatDate(value?: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}

export default function AdminPage() {
  const { user } = useUser();
  // Bumping this re-runs CompaniesSection's load() so a newly created company
  // appears instantly instead of only after a manual page refresh.
  const [refreshKey, setRefreshKey] = useState(0);

  if (!user?.is_super_admin) {
    return (
      <Shell title="Master Console" subtitle="All companies">
        <div className="panel" role="alert" style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <ShieldAlert size={20} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <strong style={{ fontSize: 16 }}>Not authorized</strong>
            <p className="muted small" style={{ margin: "4px 0 0" }}>
              The Master Console is restricted to the platform owner.
            </p>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell title="Master Console" subtitle="All companies">
      <CompaniesSection refreshKey={refreshKey} />
      <CreateCompanySection onCreated={() => setRefreshKey((k) => k + 1)} />
      <InvitesSection />
    </Shell>
  );
}

/* ─────────────────────────── Companies table ─────────────────────────── */

function CompaniesSection({ refreshKey }: { refreshKey: number }) {
  const [companies, setCompanies] = useState<AdminCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await adminApi.listCompanies();
      setCompanies(r.companies ?? []);
    } catch (e: any) {
      setError(e?.message || "Could not load companies");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // Reload when refreshKey changes (e.g. after a company is created).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  async function setStatus(id: string, status: "active" | "suspended") {
    setBusyId(id);
    setError(null);
    try {
      await adminApi.setCompanyStatus(id, status);
      await load();
    } catch (e: any) {
      setError(e?.message || "Could not update company status");
    } finally {
      setBusyId(null);
    }
  }

  const cols = "minmax(180px, 1.4fr) 130px 120px 90px 120px 120px 160px";

  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <div className="panel-header">
        <div>
          <h2><Building2 size={18} aria-hidden="true" /> Companies</h2>
        </div>
      </div>

      {error && (
        <div className="banner-warning banner-rail" role="alert" style={{ marginBottom: 12 }}>
          <p className="small" style={{ margin: 0 }}>{error}</p>
        </div>
      )}

      {loading ? (
        <p className="muted small">Loading companies…</p>
      ) : companies.length === 0 ? (
        <p className="muted small">No companies yet.</p>
      ) : (
        <div className="table-list" role="table" aria-label="Companies">
          <div className="table-row table-head" role="row" style={{ gridTemplateColumns: cols, alignItems: "center" }}>
            <span role="columnheader">Name</span>
            <span role="columnheader">Tier</span>
            <span role="columnheader">Status</span>
            <span role="columnheader">Clients</span>
            <span role="columnheader">Access</span>
            <span role="columnheader">Created</span>
            <span role="columnheader">Actions</span>
          </div>
          {companies.map((c) => {
            const isOwner = c.tier === "owner";
            const suspended = c.status === "suspended";
            return (
              <div key={c.id} className="table-row" role="row" style={{ gridTemplateColumns: cols, alignItems: "center" }}>
                <div role="cell">
                  <strong>{c.name}</strong>
                  <div className="muted small" style={{ marginTop: 2 }}>{c.slug}</div>
                </div>
                <span role="cell">{tierLabel(c.tier)}</span>
                <span role="cell"><StatusPill status={c.status} /></span>
                <span role="cell">{c.client_count}</span>
                <span role="cell" className="muted small">{c.access_type}</span>
                <span role="cell" className="muted small">{formatDate(c.created_at)}</span>
                <span role="cell">
                  {isOwner ? (
                    <span className="muted small">—</span>
                  ) : suspended ? (
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      disabled={busyId === c.id}
                      onClick={() => setStatus(c.id, "active")}
                      aria-label={`Reactivate ${c.name}`}
                    >
                      {busyId === c.id ? "Working…" : "Reactivate"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      disabled={busyId === c.id}
                      onClick={() => setStatus(c.id, "suspended")}
                      aria-label={`Suspend ${c.name}`}
                    >
                      {busyId === c.id ? "Working…" : "Suspend"}
                    </button>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── Create company ─────────────────────────── */

function CreateCompanySection({ onCreated }: { onCreated: () => void }) {
  const [businessName, setBusinessName] = useState("");
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [tier, setTier] = useState<"mentorship" | "developer">("mentorship");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdEmail, setCreatedEmail] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    // Client-side validation with specific messages (server also validates).
    if (!businessName.trim()) { setError("Business name is required."); return; }
    if (!adminName.trim()) { setError("Admin name is required."); return; }
    if (!adminEmail.trim() || !adminEmail.includes("@")) { setError("Enter a valid admin email address."); return; }
    if (adminPassword.length < 8) { setError("Password must be at least 8 characters."); return; }
    setSubmitting(true);
    setError(null);
    setCreatedEmail(null);
    try {
      const r = await adminApi.createCompany({
        company_name: businessName.trim(),
        admin_name: adminName.trim(),
        admin_email: adminEmail.trim(),
        admin_password: adminPassword,
        tier,
      });
      const email = (r as any)?.admin?.email || adminEmail.trim();
      setCreatedEmail(email);
      setBusinessName("");
      setAdminName("");
      setAdminEmail("");
      setAdminPassword("");
      setTier("mentorship");
      onCreated();
    } catch (err: any) {
      setError(err?.message || "Could not create company");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <div className="panel-header">
        <div>
          <h2><Plus size={18} aria-hidden="true" /> Create company</h2>
        </div>
      </div>

      {error && (
        <div className="banner-warning banner-rail" role="alert" style={{ marginBottom: 12 }}>
          <p className="small" style={{ margin: 0 }}>{error}</p>
        </div>
      )}

      {createdEmail && (
        <div className="banner-success banner-rail" role="status" style={{ marginBottom: 12, display: "flex", gap: 8, alignItems: "center" }}>
          <Check size={15} aria-hidden="true" />
          <p className="small" style={{ margin: 0 }}>
            Company created. Admin sign-in email: <strong>{createdEmail}</strong>
          </p>
        </div>
      )}

      <form className="stacked-form" onSubmit={submit}>
        <div className="form-row-2">
          <label className="form-field">
            <span>Business name</span>
            <input
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              required
              autoComplete="off"
            />
          </label>
          <label className="form-field">
            <span>Admin name</span>
            <input
              value={adminName}
              onChange={(e) => setAdminName(e.target.value)}
              required
              autoComplete="off"
            />
          </label>
        </div>
        <div className="form-row-2">
          <label className="form-field">
            <span>Admin email</span>
            <input
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              required
              autoComplete="off"
            />
          </label>
          <label className="form-field">
            <span>Admin password</span>
            <input
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
          </label>
        </div>
        <label className="form-field">
          <span>Tier</span>
          <select value={tier} onChange={(e) => setTier(e.target.value as "mentorship" | "developer")}>
            <option value="mentorship">Mentorship</option>
            <option value="developer">Developer</option>
          </select>
        </label>
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? "Creating…" : "Create company"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ─────────────────────────── Invites ─────────────────────────── */

function InvitesSection() {
  const [invites, setInvites] = useState<CompanyInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [tier, setTier] = useState<"mentorship" | "developer">("mentorship");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await adminApi.listInvites();
      setInvites(r.invites ?? []);
    } catch (e: any) {
      setError(e?.message || "Could not load invites");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setCreateError(null);
    setCreatedUrl(null);
    setCopied(false);
    try {
      const r = await adminApi.createInvite({
        tier,
        email: email.trim() || undefined,
        note: note.trim() || undefined,
      });
      setCreatedUrl(r.invite?.invite_url ?? null);
      setEmail("");
      setNote("");
      setTier("mentorship");
      await load();
    } catch (err: any) {
      setCreateError(err?.message || "Could not create invite");
    } finally {
      setSubmitting(false);
    }
  }

  async function copyUrl() {
    if (!createdUrl) return;
    try {
      await navigator.clipboard.writeText(createdUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be unavailable; the URL remains visible/selectable.
    }
  }

  const cols = "minmax(160px, 1.4fr) 120px minmax(160px, 1fr) 120px 110px";

  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <div className="panel-header">
        <div>
          <h2><Ticket size={18} aria-hidden="true" /> Invites</h2>
        </div>
      </div>

      {/* New invite form */}
      <form className="stacked-form" onSubmit={submit} style={{ marginBottom: 16 }}>
        {createError && (
          <div className="banner-warning banner-rail" role="alert">
            <p className="small" style={{ margin: 0 }}>{createError}</p>
          </div>
        )}
        <div className="form-row-2">
          <label className="form-field">
            <span>Tier</span>
            <select value={tier} onChange={(e) => setTier(e.target.value as "mentorship" | "developer")}>
              <option value="mentorship">Mentorship</option>
              <option value="developer">Developer</option>
            </select>
          </label>
          <label className="form-field">
            <span>Email (optional)</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="off"
              placeholder="invitee@example.com"
            />
          </label>
        </div>
        <label className="form-field">
          <span>Note (optional)</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} autoComplete="off" />
        </label>
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? "Creating…" : "New invite"}
          </button>
        </div>
      </form>

      {createdUrl && (
        <div className="banner-success banner-rail" role="status" style={{ marginBottom: 16 }}>
          <p className="small" style={{ margin: "0 0 6px" }}>Invite created. Share this link:</p>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <label className="form-field" style={{ flex: 1, minWidth: 220, margin: 0 }}>
              <span className="sr-only">Invite URL</span>
              <input
                readOnly
                value={createdUrl}
                aria-label="Invite URL"
                onFocus={(e) => e.currentTarget.select()}
              />
            </label>
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={copyUrl}
              aria-label="Copy invite URL to clipboard"
            >
              {copied ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="banner-warning banner-rail" role="alert" style={{ marginBottom: 12 }}>
          <p className="small" style={{ margin: 0 }}>{error}</p>
        </div>
      )}

      {loading ? (
        <p className="muted small">Loading invites…</p>
      ) : invites.length === 0 ? (
        <p className="muted small">No invites yet.</p>
      ) : (
        <div className="table-list" role="table" aria-label="Company invites">
          <div className="table-row table-head" role="row" style={{ gridTemplateColumns: cols, alignItems: "center" }}>
            <span role="columnheader">Token</span>
            <span role="columnheader">Tier</span>
            <span role="columnheader">Email</span>
            <span role="columnheader">Expires</span>
            <span role="columnheader">Used</span>
          </div>
          {invites.map((inv) => (
            <div key={inv.id} className="table-row" role="row" style={{ gridTemplateColumns: cols, alignItems: "center" }}>
              <span role="cell" className="muted small" style={{ fontFamily: "monospace" }}>
                {inv.token.slice(0, 12)}…
              </span>
              <span role="cell">{tierLabel(inv.tier)}</span>
              <span role="cell" className="muted small" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                {inv.email ? <><Mail size={12} aria-hidden="true" /> {inv.email}</> : "—"}
              </span>
              <span role="cell" className="muted small">{formatDate(inv.expires_at)}</span>
              <span role="cell">
                <StatusPill status={inv.used_at ? "used" : "open"} />
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
